"use client"
import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import {
  FlexCenter,
  Item,
  ItemContent,
  ItemDescription,
  ItemSide,
  ItemTitle,
  NumberDisplay,
  QuantityBadge,
  TableCell,
  TableRow,
  UnstyledButton,
} from "@opensea/ui-kit"
import { Close } from "@opensea/ui-kit/icons"
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"
import type {
  CancelOrderFlowFormValues,
  CancelOrderFlowView,
} from "../../types"
import { CANCEL_ORDER_TABLE_COLUMN_CLASSNAMES } from "../CancelOrderTable/constants"
import { CancelOrderTableRowItem } from "../CancelOrderTableRowItem"
import { CancelOrderTableRow_orderFragment } from "./CancelOrderTableRow.fragment"

type Props = {
  item: FragmentOf<typeof CancelOrderTableRow_orderFragment>
  context?: { view: CancelOrderFlowView }
  index: number
}

export const CancelOrderTableRow = ({
  item: itemKey,
  context,
  index,
}: Props) => {
  const order = readFragment(CancelOrderTableRow_orderFragment, itemKey)
  const { pricePerItem } = order
  const { control } = useFormContext<CancelOrderFlowFormValues>()
  const { remove } = useFieldArray({
    keyName: "_id",
    control,
    name: "orders",
  })

  const orders = useWatch({ control, name: "orders" })

  return (
    <TableRow>
      <TableCell className={CANCEL_ORDER_TABLE_COLUMN_CLASSNAMES.item}>
        <FlexCenter className="gap-2">
          <CancelOrderTableRowItem order={order} />
          {order.item?.isFungible || order.side === "OFFER" ? (
            <QuantityBadge>{order.quantityRemaining}</QuantityBadge>
          ) : null}
        </FlexCenter>
      </TableCell>
      <TableCell className={CANCEL_ORDER_TABLE_COLUMN_CLASSNAMES.price}>
        <Item variant="unstyled">
          <ItemContent className="items-end">
            <ItemTitle>
              <NumberDisplay
                suffix={pricePerItem.token.symbol}
                value={pricePerItem.token.unit}
              />
            </ItemTitle>
            <ItemDescription>
              <NumberDisplay display="usd" value={pricePerItem.usd} />
            </ItemDescription>
          </ItemContent>

          {orders.length > 1 && context?.view === "cancel" && (
            <ItemSide>
              <UnstyledButton
                onClick={() => {
                  remove(index)
                }}
              >
                <Close fill="interactive-primary-styles" />
              </UnstyledButton>
            </ItemSide>
          )}
        </Item>
      </TableCell>
    </TableRow>
  )
}
